import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  CircleAlert,
  CircleCheck,
  Copy,
  FileSpreadsheet,
  Pencil,
  PlugZap,
  Plus,
  Wallet,
} from 'lucide-react'
import {
  formatAmount,
  type PaymentEnvironment,
  type PaymentProvider,
  type ProviderDefinition,
} from '@concours/shared'
import {
  Alert,
  Badge,
  Button,
  Can,
  Card,
  Empty,
  Input,
  Modal,
  PageHeader,
  Select,
  Table,
  Textarea,
  useAuth,
  useToast,
  type Column,
} from '@concours/ui'
import { errorMessage, fieldErrors } from '../../lib/errors'
import { when } from '../../lib/competition-types'
import { METHODS_KEY, type MethodsResponse, type PaymentMethod } from '../../lib/payments'

/**
 * Moyens de paiement (ADR-002, ADR-026) : configuration par connecteur,
 * activation, test de connexion, URL de notification à déclarer chez le
 * prestataire, rapprochement d'un relevé. 🔒 Les secrets ne sont jamais
 * affichés : on ne peut que les remplacer.
 */
export function PaymentMethodsPage() {
  const { session } = useAuth()
  const toast = useToast()
  const queryClient = useQueryClient()
  const [editing, setEditing] = useState<PaymentMethod | 'new' | null>(null)
  const [reconciling, setReconciling] = useState<PaymentMethod | null>(null)
  const methods = useQuery({
    queryKey: METHODS_KEY,
    queryFn: () => session.api.get<MethodsResponse>('/admin/payment-methods'),
  })
  const toggle = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      session.api.patch<PaymentMethod>(`/admin/payment-methods/${id}`, { isActive }),
    onSuccess: async (m) => {
      toast.success(m.isActive ? `${m.label} activé` : `${m.label} désactivé`)
      await queryClient.invalidateQueries({ queryKey: METHODS_KEY })
    },
    onError: (error) => toast.error(errorMessage(error)),
  })
  const test = useMutation({
    mutationFn: (id: string) =>
      session.api.post<{ ok: boolean; message: string }>(`/admin/payment-methods/${id}/test`),
    onSuccess: async (r) => {
      if (r.ok) toast.success(r.message)
      else toast.error(r.message)
      await queryClient.invalidateQueries({ queryKey: METHODS_KEY })
    },
    onError: (error) => toast.error(errorMessage(error)),
  })
  const copy = (text: string) =>
    navigator.clipboard?.writeText(text).then(
      () => toast.success('Adresse copiée'),
      () => undefined
    )

  return (
    <>
      <PageHeader
        eyebrow="Administration"
        title="Moyens de paiement"
        description="Wave et Orange Money sont prêts à l’emploi : saisissez les identifiants fournis par le prestataire, testez la connexion, déclarez l’adresse de notification, puis activez le moyen. Les secrets sont chiffrés et ne sont plus jamais affichés."
        actions={
          <Can perm="payment_methods.manage">
            <Button variant="primary" icon={<Plus />} onClick={() => setEditing('new')}>
              Nouveau moyen
            </Button>
          </Can>
        }
      />
      {methods.isError && <Alert tone="danger">{errorMessage(methods.error)}</Alert>}
      {methods.data && !methods.data.data.length && (
        <Card>
          <Empty icon={<Wallet aria-hidden="true" />} title="Aucun moyen de paiement" />
        </Card>
      )}
      <div className="method-grid">
        {methods.data?.data.map((m) => (
          <Card key={m.id} className="method-card">
            <div className="method-card-head">
              <div>
                <h3>{m.label}</h3>
                <p className="text-muted" style={{ margin: 0, fontSize: 'var(--text-sm)' }}>
                  {m.providerLabel} · <span className="mono">{m.code}</span>
                </p>
              </div>
              <div className="row" style={{ gap: 'var(--space-1)', flexWrap: 'wrap' }}>
                {m.environment === 'sandbox' && <Badge variant="warning">Test</Badge>}
                <Badge variant={m.isActive ? 'success' : 'neutral'} dot>
                  {m.isActive ? 'Actif' : 'Inactif'}
                </Badge>
              </div>
            </div>
            {m.missing.length > 0 && (
              <Alert tone="warning" title="Configuration incomplète">
                À renseigner : {m.missing.join(', ')}.
              </Alert>
            )}
            <dl className="summary-list">
              <div>
                <dt>Frais du moyen</dt>
                <dd>
                  {m.fees.fixed || m.fees.percent
                    ? [
                        m.fees.fixed ? formatAmount(m.fees.fixed, m.currency) : null,
                        m.fees.percent ? `${String(m.fees.percent).replace('.', ',')} %` : null,
                      ]
                        .filter(Boolean)
                        .join(' + ')
                    : 'Aucun'}
                </dd>
              </div>
              <div>
                <dt>Remboursement</dt>
                <dd>
                  {m.refundable ? 'Par l’API du prestataire' : 'Hors plateforme (enregistré)'}
                </dd>
              </div>
              <div>
                <dt>Dernier test</dt>
                <dd>
                  {m.lastTest ? (
                    <span className="row" style={{ gap: 'var(--space-1)' }}>
                      {m.lastTest.ok ? (
                        <CircleCheck aria-hidden="true" size={14} className="text-success" />
                      ) : (
                        <CircleAlert aria-hidden="true" size={14} className="text-danger" />
                      )}
                      {when(m.lastTest.at)} — {m.lastTest.message}
                    </span>
                  ) : (
                    'Jamais'
                  )}
                </dd>
              </div>
              {m.openPayments ? (
                <div>
                  <dt>En cours</dt>
                  <dd>{m.openPayments} paiement(s)</dd>
                </div>
              ) : null}
            </dl>
            {m.provider !== 'fake' && (
              <div>
                <p className="field-label" style={{ marginBlockEnd: 'var(--space-1)' }}>
                  Adresse de notification (à déclarer chez le prestataire)
                </p>
                <div className="method-url">
                  <span className="grow">{m.webhookUrl}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    icon={<Copy />}
                    aria-label="Copier l’adresse de notification"
                    onClick={() => void copy(m.webhookUrl)}
                  />
                </div>
              </div>
            )}
            <div className="row" style={{ gap: 'var(--space-2)', flexWrap: 'wrap' }}>
              <Can perm="payment_methods.manage">
                <label className="check-row">
                  <input
                    type="checkbox"
                    checked={m.isActive}
                    disabled={toggle.isPending}
                    onChange={(e) => toggle.mutate({ id: m.id, isActive: e.target.checked })}
                  />
                  <span>Proposé aux candidats</span>
                </label>
                <Button
                  variant="secondary"
                  size="sm"
                  icon={<Pencil />}
                  onClick={() => setEditing(m)}
                >
                  Configurer
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  icon={<PlugZap />}
                  loading={test.isPending && test.variables === m.id}
                  onClick={() => test.mutate(m.id)}
                >
                  Tester la connexion
                </Button>
              </Can>
              <Can perm="payment.reconcile">
                <Button
                  variant="ghost"
                  size="sm"
                  icon={<FileSpreadsheet />}
                  onClick={() => setReconciling(m)}
                >
                  Rapprocher un relevé
                </Button>
              </Can>
            </div>
          </Card>
        ))}
      </div>
      {editing && methods.data && (
        <MethodModal
          method={editing === 'new' ? null : editing}
          providers={methods.data.providers}
          environments={methods.data.environments}
          onClose={() => setEditing(null)}
        />
      )}
      {reconciling && <ReconcileModal method={reconciling} onClose={() => setReconciling(null)} />}
    </>
  )
}

/* ------------------------------------------------------------------ */

function MethodModal({
  method,
  providers,
  environments,
  onClose,
}: {
  method: PaymentMethod | null
  providers: Record<PaymentProvider, ProviderDefinition>
  environments: Record<PaymentEnvironment, string>
  onClose: () => void
}) {
  const { session } = useAuth()
  const toast = useToast()
  const queryClient = useQueryClient()
  const [provider, setProvider] = useState<PaymentProvider>(method?.provider ?? 'wave')
  const [code, setCode] = useState('')
  const [label, setLabel] = useState(method?.label ?? providers[provider]?.label ?? '')
  const [description, setDescription] = useState(method?.description ?? '')
  const [environment, setEnvironment] = useState<PaymentEnvironment>(
    method?.environment ?? 'sandbox'
  )
  const [feeFixed, setFeeFixed] = useState(String(method?.fees.fixed ?? 0))
  const [feePercent, setFeePercent] = useState(String(method?.fees.percent ?? 0))
  const [config, setConfig] = useState<Record<string, string>>(
    Object.fromEntries(Object.entries(method?.config ?? {}).map(([k, v]) => [k, v ?? '']))
  )
  // Secrets : vide = inchangé ; on ne peut que remplacer.
  const [secrets, setSecrets] = useState<Record<string, string>>({})
  const [replacing, setReplacing] = useState<Record<string, boolean>>({})
  const definition = providers[provider]

  const save = useMutation({
    mutationFn: () => {
      const body = {
        label,
        description: description || null,
        environment,
        feeFixed: Number(feeFixed) || 0,
        feePercent: Number(String(feePercent).replace(',', '.')) || 0,
        config: Object.fromEntries(
          definition.fields
            .filter((f) => !f.secret)
            .map((f) => [f.key, config[f.key]?.trim() ? config[f.key]!.trim() : null])
        ),
        secrets: Object.fromEntries(Object.entries(secrets).filter(([, v]) => v.trim())),
      }
      return method
        ? session.api.patch<PaymentMethod>(`/admin/payment-methods/${method.id}`, body)
        : session.api.post<PaymentMethod>('/admin/payment-methods', { ...body, code, provider })
    },
    onSuccess: async (m) => {
      toast.success(method ? 'Moyen de paiement enregistré' : `${m.label} créé (inactif)`)
      await queryClient.invalidateQueries({ queryKey: METHODS_KEY })
      onClose()
    },
    onError: (error) => {
      if (!Object.keys(fieldErrors(error)).length) toast.error(errorMessage(error))
    },
  })
  const errors = fieldErrors(save.error)

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title={method ? `Configurer ${method.label}` : 'Nouveau moyen de paiement'}
      description={definition?.description}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Annuler
          </Button>
          <Button variant="primary" loading={save.isPending} onClick={() => save.mutate()}>
            Enregistrer
          </Button>
        </>
      }
    >
      <div className="stack" style={{ gap: 'var(--space-4)' }}>
        {Object.keys(errors).length > 0 && (
          <Alert tone="danger">{Object.values(errors).join(' ')}</Alert>
        )}
        {!method && (
          <div className="grid-2">
            <Select
              label="Connecteur"
              value={provider}
              onChange={(e) => {
                const next = e.target.value as PaymentProvider
                setProvider(next)
                setLabel(providers[next].label)
                setConfig({})
                setSecrets({})
              }}
              options={Object.entries(providers).map(([value, p]) => ({ value, label: p.label }))}
            />
            <Input
              label="Code"
              required
              mono
              hint="Identifiant technique (minuscules, chiffres, tirets)."
              value={code}
              error={errors.code}
              onChange={(e) => setCode(e.target.value)}
            />
          </div>
        )}
        <div className="grid-2">
          <Input
            label="Libellé affiché aux candidats"
            required
            value={label}
            error={errors.label}
            onChange={(e) => setLabel(e.target.value)}
          />
          <Select
            label="Environnement"
            value={environment}
            onChange={(e) => setEnvironment(e.target.value as PaymentEnvironment)}
            options={Object.entries(environments)
              .filter(([value]) => provider !== 'fake' || value === 'sandbox')
              .map(([value, text]) => ({ value, label: text }))}
          />
        </div>
        <Textarea
          label="Description (facultatif)"
          rows={2}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
        <div className="grid-2">
          <Input
            label="Frais fixes (F CFA)"
            inputMode="numeric"
            value={feeFixed}
            hint="Ajoutés aux frais d’inscription."
            onChange={(e) => setFeeFixed(e.target.value.replace(/\D/g, ''))}
          />
          <Input
            label="Frais proportionnels (%)"
            inputMode="decimal"
            value={feePercent}
            hint="Arrondis à l’unité supérieure. 0 si le marchand prend les frais."
            onChange={(e) => setFeePercent(e.target.value)}
          />
        </div>

        {definition?.fields.length > 0 && (
          <fieldset className="stack" style={{ gap: 'var(--space-3)', border: 0, padding: 0 }}>
            <legend className="summary-title">Identifiants {definition.label}</legend>
            {definition.fields.map((f) =>
              f.secret ? (
                method?.secrets[f.key]?.set && !replacing[f.key] ? (
                  <div key={f.key} className="field">
                    <span className="field-label">{f.label}</span>
                    <div className="secret-field">
                      <span>
                        Défini{' '}
                        <span className="mono text-muted">{method.secrets[f.key]!.hint}</span>
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setReplacing({ ...replacing, [f.key]: true })}
                      >
                        Remplacer
                      </Button>
                    </div>
                  </div>
                ) : (
                  <Input
                    key={f.key}
                    label={f.label}
                    type="password"
                    autoComplete="new-password"
                    required={f.required}
                    hint={f.help}
                    value={secrets[f.key] ?? ''}
                    error={errors[`secrets.${f.key}`]}
                    onChange={(e) => setSecrets({ ...secrets, [f.key]: e.target.value })}
                  />
                )
              ) : (
                <Input
                  key={f.key}
                  label={f.label}
                  required={f.required}
                  hint={f.help}
                  inputMode={f.type === 'number' ? 'numeric' : undefined}
                  value={config[f.key] ?? ''}
                  error={errors[`config.${f.key}`]}
                  onChange={(e) => setConfig({ ...config, [f.key]: e.target.value })}
                />
              )
            )}
          </fieldset>
        )}
      </div>
    </Modal>
  )
}

/* ------------------------------------------------------------------ */

interface ReconcileReport {
  lines: number
  matched: { reference: string; providerTxnId: string; amount: number }[]
  mismatched: {
    reference: string
    providerTxnId: string
    expected: number
    found: number
    status: string
  }[]
  unknown: { providerTxnId: string; reference: string | null; amount: number }[]
  missing: {
    reference: string
    providerTxnId: string | null
    amount: number
    paidAt: string | null
  }[]
}

type ReportRow = { key: string; kind: string; reference: string; txn: string; detail: string }

function ReconcileModal({ method, onClose }: { method: PaymentMethod; onClose: () => void }) {
  const { session } = useAuth()
  const [file, setFile] = useState<File | null>(null)
  const run = useMutation({
    mutationFn: () => {
      const body = new FormData()
      body.append('file', file!)
      return session.api.post<ReconcileReport>(
        `/admin/payment-methods/${method.id}/reconcile`,
        body
      )
    },
  })
  const r = run.data
  const rows: ReportRow[] = r
    ? [
        ...r.mismatched.map((m) => ({
          key: `x-${m.providerTxnId}`,
          kind: 'Écart',
          reference: m.reference,
          txn: m.providerTxnId,
          detail: `Attendu ${formatAmount(m.expected)}, relevé ${formatAmount(m.found)} (${m.status})`,
        })),
        ...r.unknown.map((u) => ({
          key: `u-${u.providerTxnId}`,
          kind: 'Inconnue',
          reference: u.reference ?? '—',
          txn: u.providerTxnId,
          detail: `${formatAmount(u.amount)} — absente de la plateforme`,
        })),
        ...r.missing.map((m) => ({
          key: `m-${m.reference}`,
          kind: 'Absente du relevé',
          reference: m.reference,
          txn: m.providerTxnId ?? '—',
          detail: `${formatAmount(m.amount)} confirmé le ${when(m.paidAt)}`,
        })),
      ]
    : []
  const columns: Column<ReportRow>[] = [
    { key: 'kind', header: 'Constat', cell: (x) => <Badge variant="warning">{x.kind}</Badge> },
    { key: 'ref', header: 'Référence', cell: (x) => <span className="mono">{x.reference}</span> },
    { key: 'txn', header: 'Transaction', cell: (x) => <span className="mono">{x.txn}</span> },
    { key: 'detail', header: 'Détail', cell: (x) => x.detail },
  ]
  return (
    <Modal
      open
      onClose={onClose}
      size="xl"
      title={`Rapprocher un relevé — ${method.label}`}
      description="Relevé CSV exporté du portail du prestataire : colonnes « transaction_id » (ou « transaction ») et « montant » (ou « amount »), « reference » conseillée."
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Fermer
          </Button>
          <Button
            variant="primary"
            disabled={!file}
            loading={run.isPending}
            onClick={() => run.mutate()}
          >
            Rapprocher
          </Button>
        </>
      }
    >
      <div className="stack" style={{ gap: 'var(--space-4)' }}>
        <div className="field">
          <label className="field-label" htmlFor="statement">
            Relevé (CSV)
          </label>
          <input
            id="statement"
            type="file"
            accept=".csv,text/csv"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
        </div>
        {run.isError && <Alert tone="danger">{errorMessage(run.error)}</Alert>}
        {r && (
          <>
            <Alert tone={rows.length ? 'warning' : 'success'} title="Résultat">
              {r.lines} ligne(s) lue(s) : {r.matched.length} concordante(s), {r.mismatched.length}{' '}
              écart(s), {r.unknown.length} inconnue(s), {r.missing.length} paiement(s) confirmé(s)
              absent(s) du relevé.
            </Alert>
            {rows.length > 0 && (
              <Table columns={columns} rows={rows} rowKey={(x) => x.key} caption="Écarts" />
            )}
          </>
        )}
      </div>
    </Modal>
  )
}
